import { rgb } from "pdf-lib";
import { wrapText } from "./pdfTextFitting";
import { formatLongDate } from "../utils";
import { toWinAnsiText } from "./pdfTextEncoding";

const MARGIN = 54; // 0.75"

const HEADER_SIZE = 11;
const HEADER_LINE_HEIGHT = 15;
const HEADER_GAP = 18; // space between the header block and the first section

const TITLE_SIZE = 11.5;
const TITLE_LINE_HEIGHT = 15;
const TITLE_GAP = 6; // space between a section title and its body

const BODY_SIZE = 9.5;
const BODY_LINE_HEIGHT = BODY_SIZE * 1.35;

const RULE_GAP_ABOVE = 12;
const RULE_GAP_BELOW = 14;

/**
 * Builds the two header lines repeated on every addendum page. Segments whose
 * data is missing are simply omitted.
 */
const buildHeaderLines = ({ formCode, clientName, aNumber, date }) => {
    const identity = [
        clientName ? `Name: ${clientName}` : "",
        aNumber ? `A-Number: ${aNumber}` : "",
        date ? `Date: ${formatLongDate(date)}` : "",
    ].filter(Boolean);

    return [identity.join("    "), `Addendum for Form${formCode ? ` ${formCode}` : ""}`].filter(Boolean).map(toWinAnsiText);
};

/**
 * Renders the addendum as brand new pages appended at the end of the document.
 *
 * Must be called AFTER form.flatten() so the appended pages carry no AcroForm
 * widgets of their own.
 *
 * @param {PDFDocument} pdfDoc
 * @param {Array<{ label: string, text: string }>} entries - already ordered
 * @param {{ formCode?: string, clientName?: string, aNumber?: string, date?: Date }} info
 * @param {{ body: PDFFont, title: PDFFont, headerBold: PDFFont }} fonts
 * @returns {number} Number of pages added
 */
export const renderAddendum = (pdfDoc, entries, info, fonts) => {
    if (!entries || entries.length === 0) return 0;

    const [firstPage] = pdfDoc.getPages();
    const { width, height } = firstPage ? firstPage.getSize() : { width: 612, height: 792 };
    const contentWidth = width - MARGIN * 2;
    const bottomLimit = MARGIN;

    const headerLines = buildHeaderLines(info || {});

    let page = null;
    let cursorY = 0;
    let pagesAdded = 0;

    const startNewPage = () => {
        page = pdfDoc.addPage([width, height]);
        pagesAdded++;
        cursorY = height - MARGIN;

        headerLines.forEach((line) => {
            page.drawText(line, {
                x: MARGIN,
                y: cursorY - HEADER_SIZE,
                size: HEADER_SIZE,
                font: fonts.headerBold,
                color: rgb(0, 0, 0),
            });
            cursorY -= HEADER_LINE_HEIGHT;
        });

        cursorY -= HEADER_GAP;
    };

    const ensureSpace = (needed) => {
        if (!page || cursorY - needed < bottomLimit) startNewPage();
    };

    entries.forEach((entry) => {
        const label = toWinAnsiText(entry.label);
        const bodyLines = wrapText(toWinAnsiText(entry.text), fonts.body, BODY_SIZE, contentWidth);

        // Keep the title with at least the first two lines of its body.
        ensureSpace(TITLE_LINE_HEIGHT + TITLE_GAP + BODY_LINE_HEIGHT * Math.min(2, bodyLines.length));

        page.drawText(label, {
            x: MARGIN,
            y: cursorY - TITLE_SIZE,
            size: TITLE_SIZE,
            font: fonts.title,
            color: rgb(0, 0, 0),
        });
        cursorY -= TITLE_LINE_HEIGHT + TITLE_GAP;

        // The body flows across pages: the header repeats, the title does not.
        bodyLines.forEach((line) => {
            ensureSpace(BODY_LINE_HEIGHT);
            if (line !== "") {
                page.drawText(line, {
                    x: MARGIN,
                    y: cursorY - BODY_SIZE,
                    size: BODY_SIZE,
                    font: fonts.body,
                    color: rgb(0, 0, 0),
                });
            }
            cursorY -= BODY_LINE_HEIGHT;
        });

        // Separator rule closing the section. It stays on the page where the body
        // ended - pushing it to the next page would orphan it under the header.
        const ruleY = Math.max(cursorY - RULE_GAP_ABOVE, bottomLimit);
        page.drawLine({
            start: { x: MARGIN, y: ruleY },
            end: { x: MARGIN + contentWidth, y: ruleY },
            thickness: 1,
            color: rgb(0, 0, 0),
        });
        cursorY = ruleY - RULE_GAP_BELOW;
    });

    return pagesAdded;
};
